import React, { useCallback } from 'react';
import { ExpenseWithDetails } from './types';
import { expensesApi } from './services/expenseApi';
import { SearchSection } from '../../../components/common/SearchSection';
import './ExpenseSearchSection.css';

interface ExpenseSearchSectionProps {
  selectedExpenseIds: number[];
  onExpensesChange: (expenseIds: number[]) => void;
  label?: string; // Custom label for the search section
  placeholder?: string; // Custom placeholder text
  maxSelections?: number; // Maximum number of expenses that can be selected (default: unlimited)
}

const ExpenseSearchSection: React.FC<ExpenseSearchSectionProps> = ({
  selectedExpenseIds,
  onExpensesChange,
  label = "Search Expenses",
  placeholder = "Search by description, date, or amount...",
  maxSelections
}) => {
  const searchFunction = useCallback(async (searchTerm: string) => {
    const response = await expensesApi.getAll({
      search: searchTerm.trim(),
      limit: 10
    });
    return response.expenses;
  }, []);

  const formatExpenseDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const renderResultItem = React.useCallback((expense: ExpenseWithDetails, onAdd: () => void, isDisabled?: boolean) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '12px', 
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: '#ffffff'
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
          {expense.description || 'No description'}
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
          {expense.vendorName || expense.vendorBusinessName || 'Unknown vendor'}
        </div>
        <div style={{ fontSize: '11px', color: '#888' }}>
          📅 {formatExpenseDate(expense.expenseDate)} • 💰 {formatAmount(expense.amount)}
        </div>
      </div>
      <button
        type="button"
        className="app-btn app-btn-primary btn-small"
        onClick={onAdd}
        style={{ marginLeft: '8px' }}
        disabled={isDisabled}
      >
        Add
      </button>
    </div>
  ), []);

  const renderSelectedItem = React.useCallback((expense: ExpenseWithDetails, onRemove: () => void) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '8px 12px', 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #e9ecef', 
      borderRadius: '4px', 
      marginBottom: '0.5rem' 
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
          {expense.description || 'No description'}
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
          {expense.vendorName || expense.vendorBusinessName || 'Unknown vendor'}
        </div>
        <div style={{ fontSize: '11px', color: '#888' }}>
          📅 {formatExpenseDate(expense.expenseDate)} • 💰 {formatAmount(expense.amount)}
        </div>
      </div>
      <button
        type="button"
        className="app-btn app-btn-danger btn-small"
        onClick={onRemove}
        title="Remove expense"
        style={{ marginLeft: '8px' }}
      >
        🗑️
      </button>
    </div>
  ), []);

  return (
    <SearchSection<ExpenseWithDetails>
      selectionMode="multiple"
      selectedIds={selectedExpenseIds}
      onSelectionChange={onExpensesChange}
      searchFunction={searchFunction}
      getByIdFunction={expensesApi.getById}
      renderResultItem={renderResultItem}
      renderSelectedItem={renderSelectedItem}
      getItemId={useCallback((expense: ExpenseWithDetails) => expense.id, [])}
      label={label}
      placeholder={placeholder}
      maxSelections={maxSelections}
      entityType="expense"
      emptyStateMessage="No expenses selected."
      selectedLabel="Selected Expenses"
    />
  );
};

export default ExpenseSearchSection;
